
export const TimelineSection = () => {
  const timeline = [
    {
      quarter: '2024 Q1',
      status: 'completed',
      title: 'Series A 펀딩 시작',
      description: '펀딩 라운드 시작 및 초기 투자자 모집',
      milestones: ['투자자 프레젠테이션', '법적 문서 준비', '디드딜리전스 시작']
    },
    {
      quarter: '2024 Q2',
      status: 'current',
      title: '제품 상용화 준비',
      description: '대량 생산을 위한 시설 확장 및 인력 충원',
      milestones: ['생산 시설 확장', '품질 인증 획득', 'FDA 승인 진행']
    },
    {
      quarter: '2024 Q3',
      status: 'upcoming',
      title: '시장 출시',
      description: '첫 제품 라인 상용화 및 판매 시작',
      milestones: ['제품 출시', '마케팅 캠페인', '유통 네트워크 구축']
    },
    {
      quarter: '2024 Q4',
      status: 'upcoming',
      title: '해외 진출',
      description: '북미 시장 진출 및 글로벌 파트너십 구축',
      milestones: ['해외 인증 완료', '현지 파트너 계약', '수출 시작']
    },
    {
      quarter: '2025 Q1',
      status: 'upcoming',
      title: 'Series B 준비',
      description: '추가 펀딩 라운드 준비 및 사업 확장',
      milestones: ['Series B 펀딩', '신제품 개발', '연구소 확장']
    },
    {
      quarter: '2025 Q2',
      status: 'upcoming',
      title: 'IPO 로드맵',
      description: '기업공개 준비 및 상장 절차 시작',
      milestones: ['IPO 준비', '회계 감사', '투자은행 선정']
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'current':
        return 'bg-blue-500';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'ri-check-line';
      case 'current':
        return 'ri-play-circle-line';
      default:
        return 'ri-time-line';
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            사업 실행 로드맵
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            체계적이고 단계적인 사업 계획으로 안정적인 성장과 수익 창출을 목표로 합니다.
          </p>
        </div>

        <div className="relative">
          {/* 타임라인 라인 */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

          <div className="space-y-12">
            {timeline.map((item, index) => (
              <div key={index} className="relative flex items-start">
                {/* 타임라인 포인트 */}
                <div className={`relative z-10 flex items-center justify-center w-16 h-16 rounded-full ${getStatusColor(item.status)}`}>
                  <i className={`${getStatusIcon(item.status)} text-xl text-white`}></i>
                </div>

                {/* 콘텐츠 */}
                <div className="ml-8 flex-1">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        item.status === 'completed' ? 'bg-green-100 text-green-800' :
                        item.status === 'current' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {item.quarter}
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{item.description}</p>
                    
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 text-sm">주요 마일스톤:</h4>
                      <ul className="space-y-1">
                        {item.milestones.map((milestone, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-700">
                            <i className={`ri-${item.status === 'completed' ? 'check' : 'arrow-right'}-line text-${item.status === 'completed' ? 'green' : 'blue'}-600 mr-2`}></i>
                            {milestone}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 진행 상황 요약 */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-center mb-12">현재 진행 상황</h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">18개월</div>
              <div className="text-blue-100">개발 기간</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">72%</div>
              <div className="text-blue-100">펀딩 달성률</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">3건</div>
              <div className="text-blue-100">인증 진행중</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">45일</div>
              <div className="text-blue-100">펀딩 마감까지</div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-lg text-blue-100 mb-6">
              현재까지 모든 마일스톤을 계획대로 달성하고 있으며, 
              예정된 일정보다 앞서 진행되고 있습니다.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
